/**
 * PhotoLightbox — full-screen image gallery overlay.
 *
 * Click any thumbnail in the dog sidebar → lightbox opens with the
 * full-size image, prev/next navigation, and keyboard support
 * (Esc to close, ← / → to navigate).
 *
 * Lazy: only renders the active image at full resolution. Adjacent
 * images get the thumb URL so swiping through a 12-photo gallery
 * stays snappy on mobile.
 */
import React, { useEffect, useCallback } from "react";
import { CaretLeftIcon as CaretLeft, CaretRightIcon as CaretRight, XIcon as X } from "@phosphor-icons/react";
import { photoUrl } from "@/lib/utils-app";

export default function PhotoLightbox({ photos, openIndex, onClose, onChange }) {
  const open = openIndex !== null && openIndex >= 0 && photos && photos[openIndex];

  const go = useCallback((delta) => {
    if (!photos?.length) return;
    const next = (openIndex + delta + photos.length) % photos.length;
    onChange?.(next);
  }, [openIndex, photos, onChange]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    // Prevent background scrolling while open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, go, onClose]);

  if (!open) return null;
  const src = photoUrl(photos[openIndex]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Photo ${openIndex + 1} of ${photos.length}`}
      data-testid="photo-lightbox"
      className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
    >
      <button
        type="button"
        aria-label="Close"
        data-testid="photo-lightbox-close"
        onClick={(e) => { e.stopPropagation(); onClose?.(); }}
        className="absolute top-4 right-4 inline-flex items-center justify-center h-10 w-10 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors backdrop-blur-md"
        style={{ marginTop: "env(safe-area-inset-top)" }}
      >
        <X size={20} weight="bold" />
      </button>

      {photos.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Previous"
            data-testid="photo-lightbox-prev"
            onClick={(e) => { e.stopPropagation(); go(-1); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-12 w-12 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors backdrop-blur-md"
          >
            <CaretLeft size={22} weight="bold" />
          </button>
          <button
            type="button"
            aria-label="Next"
            data-testid="photo-lightbox-next"
            onClick={(e) => { e.stopPropagation(); go(1); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-12 w-12 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors backdrop-blur-md"
          >
            <CaretRight size={22} weight="bold" />
          </button>
        </>
      )}

      <img
        src={src}
        alt=""
        onClick={(e) => e.stopPropagation()}
        className="max-w-[95vw] max-h-[88vh] object-contain shadow-2xl rounded-md"
      />

      {photos.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-white/10 text-white text-[11px] font-bold tracking-[0.12em] backdrop-blur-md">
          {openIndex + 1} / {photos.length}
        </div>
      )}
    </div>
  );
}
